/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import Spinner from "../components/Spinner";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import UriEntry, { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import TimingEntry, { displayStringFromFHIRTiming } from "../datatypeComponents/TimingEntry";
import QuantityEntry, { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import RangeEntry, { displayStringFromFHIRRange } from "../datatypeComponents/RangeEntry";
import BooleanEntry, { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import CodeableReferenceEntry, { DisplayFromFHIRCodeableReference } from "../datatypeComponents/CodeableReferenceEntry";
import DosageInstructionEntry, { displayStringFromFHIRDosage, displayStringFromFHIRDosageInstruction } from "../datatypeComponents/DosageEntry";
import ExpressionEntry, { DisplayFromFHIRExpression } from "../datatypeComponents/ExpressionEntry";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import StringEntry from "../datatypeComponents/StringEntry";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";

const activityDefinitionDotKindValues = ['Appointment', 'AppointmentResponse', 'CarePlan', 'Claim', 'CommunicationRequest', 'Contract', 'CoverageEligibilityRequest', 'DeviceRequest', 'EnrollmentRequest', 'ImmunizationRecommendation', 'MedicationRequest', 'NutritionOrder', 'RequestOrchestration', 'ServiceRequest', 'SupplyRequest', 'VisionPrescription'];
const activityDefinitionDotCodeSystemChoices = [{ 'uri': 'http://www.nlm.nih.gov/research/umls/rxnorm', 'display': 'RxNorm' }, { 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }, { 'uri': 'http://www.whocc.no/atc', 'display': 'ATC' }];
const activityDefinitionDotIntentValues = ['proposal', 'plan', 'directive', 'order', 'original-order', 'reflex-order', 'filler-order', 'instance-order', 'option'];
const activityDefinitionDotPriorityValues = ['routine', 'urgent', 'asap', 'stat'];
const activityDefinitionDotAsNeededCodeableConceptSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const activityDefinitionDotLocationResourceTypes = ['Location'];
const activityDefinitionDotProductCodeableConceptSystemChoices = [{ 'uri': 'http://www.nlm.nih.gov/research/umls/rxnorm', 'display': 'RxNorm' }, { 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }, { 'uri': 'http://www.whocc.no/atc', 'display': 'ATC' }, { 'uri': 'https://wiki.vnr.fi/', 'display': 'Nordic Article Number (Vnr)' }];
const activityDefinitionDotSubjectReferenceResourceTypes = ['Group', 'MedicinalProductDefinition', 'SubstanceDefinition', 'AdministrableProductDefinition', 'ManufacturedItemDefinition', 'PackagedProductDefinition'];
const activityDefinitionDotSubjectCodeableConceptValueSet = [
    { system: "http://hl7.org/fhir/fhir-types", code: "CareTeam", display: "CareTeam" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Device", display: "Device" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Group", display: "Group" },
    { system: "http://hl7.org/fhir/fhir-types", code: "HealthcareService", display: "HealthcareService" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Location", display: "Location" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Organization", display: "Organization" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Patient", display: "Patient" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Practitioner", display: "Practitioner" },
    { system: "http://hl7.org/fhir/fhir-types", code: "PractitionerRole", display: "PractitionerRole" },
    { system: "http://hl7.org/fhir/fhir-types", code: "RelatedPerson", display: "RelatedPerson" }
];
const activityDefinitionDotProductReferenceResourceTypes = ['Medication', 'Substance', 'Ingredient'];
const activityDefinitionDotSubjectAllowedDatatypes = ['CodeableConcept', 'Reference', 'canonical'];
const activityDefinitionDotTimingAllowedDatatypes = ['Timing', 'Age', 'Range', 'Duration'];
const activityDefinitionDotAsNeededAllowedDatatypes = ['boolean', 'CodeableConcept'];
const activityDefinitionDotProductAllowedDatatypes = ['Reference', 'CodeableConcept'];
const activityDefinitionDotBodySiteSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];

const generateNarrativeForActivityDefinitionResource = (resource) => {
    let status = "generated";
    let innerDiv = "";
    if (resource.title) {
        innerDiv += "<p><b>Title: </b>" + resource.title + "</p>";
    }
    if (resource.subtitle) {
        innerDiv += "<p><b>Subtitle: </b>" + resource.subtitle + "</p>";
    }
    if (resource.name) {
        innerDiv += "<p><b>Name: </b>" + resource.name + "</p>";
    }
    if (resource.description) {
        innerDiv += "<p><b>Description: </b>" + resource.description + "</p>";
    }
    if (resource.subjectCodeableConcept) {
        innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRCodeableConcept(resource.subjectCodeableConcept) + "</p>";
    }
    if (resource.subjectReference) {
        innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRReference(resource.subjectReference) + "</p>";
    }
    if (resource.subjectCanonical) {
        innerDiv += "<p><b>Subject: </b>" + resource.subjectCanonical + "</p>";
    }
    if (resource.code) {
        innerDiv += "<p><b>Code: </b>" + displayStringFromFHIRCodeableConcept(resource.code) + "</p>";
    }
    if (resource.doNotPerform) {
        innerDiv += "<p><b>Negation: DO NOT PEFORM</b></p>";
    }
    if (resource.timingTiming) {
        innerDiv += "<p><b>Timing: </b>" + displayStringFromFHIRTiming(resource.timingTiming) + "</p>";
    }
    if (resource.timingAge) {
        innerDiv += "<p><b>Timing: </b>" + displayStringFromFHIRQuantity(resource.timingAge) + "</p>";
    }
    if (resource.timingRange) {
        innerDiv += "<p><b>Timing: </b>" + displayStringFromFHIRRange(resource.timingRange) + "</p>";
    }
    if (resource.timingDuration) {
        innerDiv += "<p><b>Timing: </b>" + displayStringFromFHIRQuantity(resource.timingDuration) + "</p>";
    }
    if (resource.asNeededBoolean !== undefined) {
        innerDiv += "<p><b>As needed: </b>" + displayStringFromFHIRBoolean(resource.asNeededBoolean) + "</p>";
    }
    if (resource.asNeededCodeableConcept) {
        innerDiv += "<p><b>As needed for: </b>" + displayStringFromFHIRCodeableConcept(resource.asNeededCodeableConcept) + "</p>";
    }
    if (resource.productReference) {
        innerDiv += "<p><b>Product: </b>" + displayStringFromFHIRReference(resource.productReference) + "</p>";
    }
    if (resource.productCodeableConcept) {
        innerDiv += "<p><b>Product: </b>" + displayStringFromFHIRCodeableConcept(resource.productCodeableConcept) + "</p>";
    }
    if (resource.quantity) {
        innerDiv += "<p><b>Quantity: </b>" + displayStringFromFHIRQuantity(resource.quantity) + "</p>";
    }
    if (resource.dosage?.length > 0) {
        innerDiv += resource.dosage.map((dosage) => {
            return "<p><b>Dosage: </b>" + displayStringFromFHIRDosage(dosage) + "</p>"
        }).join("");
    }
    if (resource.dosageInstruction) {
        innerDiv += "<p><b>Dosage: </b>" + displayStringFromFHIRDosageInstruction(resource.dosageInstruction) + "</p>"
    }
    if (resource.bodySite?.length > 0) {
        innerDiv += resource.bodySite.map((bodySite) => {
            return "<p><b>Body Site: </b>" + displayStringFromFHIRCodeableConcept(bodySite) + "</p>"
        }).join("");
    }
    return { "status": status, "div": '<div xmlns="http://www.w3.org/1999/xhtml">' + innerDiv + "</div>" };
}

const ActivityDefinitionDynamicValueEntry = ({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed }) => {

    const [dynamicValueState, setDynamicValueState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingDynamicValue = { path: "", expression: {} };
        if (startingValue) {
            if (startingValue.extension) { startingDynamicValue.extension = startingValue.extension; }
            if (startingValue.path) { startingDynamicValue.path = startingValue.path; }
            if (startingValue.expression) { startingDynamicValue.expression = startingValue.expression; }
        }
        setDynamicValueState(JSON.parse(JSON.stringify(startingDynamicValue)));
    }, []);

    useEffect((() => {
        if (Object.keys(dynamicValueState).length) {
            let newDynamicValue = {};
            if (dynamicValueState.extension) { newDynamicValue.extension = dynamicValueState.extension; }
            if (dynamicValueState.path) { newDynamicValue.path = dynamicValueState.path; }
            if (dynamicValueState.expression && Object.keys(dynamicValueState.expression).length) {
                newDynamicValue.expression = dynamicValueState.expression;
            }
            if (Object.keys(newDynamicValue).length === 0) {
                newDynamicValue = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newDynamicValue }; });
        }
    }), [dynamicValueState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.path && <p><b>Path: </b>{startingValue.path}</p>}
                {startingValue.expression && <div><b>Expression: </b><DisplayFromFHIRExpression expression={startingValue.expression} /></div>}
                <br />
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(dynamicValueState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='path' fieldLabel='Path'
                    startingValue={dynamicValueState.path} setResourceState={setDynamicValueState} />
                <ExpressionEntry elementName='expression' fieldLabel='Expression'
                    startingValue={dynamicValueState.expression} setResourceState={setDynamicValueState} />
            </div>
        </>
    }
};

const activityDefinitionDotParticipantDotTypeValues = ["careteam", "device", "group", "healthcareservice", "location", "organization", "patient", "practitioner", "practitionerrole", "relatedperson"];
const activityDefinitionDotParticipantDotTypeReferenceResourceTypes = ['CareTeam', 'Device', 'DeviceDefinition', 'Endpoint', 'Group', 'HealthcareService', 'Location', 'Organization', 'Patient', 'Practitioner', 'PractitionerRole', 'RelatedPerson']
const activityDefinitionDotParticipantDotRoleSystemChoices = [{ 'uri': 'http://terminology.hl7.org/CodeSystem/practitioner-role', 'display': 'HL7 FHIR Practitioner Role' }, { 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }, { 'uri': 'http://terminology.hl7.org/CodeSystem/v3-RoleCode', 'display': 'HL7 V3 Role Code' }]
const activityDefinitionDotParticipantDotFunctionValueSet = [
    { system: "http://hl7.org/fhir/action-participant-function", code: "performer", display: "Perfomer" },
    { system: "http://hl7.org/fhir/action-participant-function", code: "author", display: "Author" },
    { system: "http://hl7.org/fhir/action-participant-function", code: "reviewer", display: "Reviewer" },
    { system: "http://hl7.org/fhir/action-participant-function", code: "witness", display: "Witness" }
]
const ActivityDefinitionParticipantEntry = ({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed, containedResourceDictionary = {} }) => {

    const [participantState, setParticipantState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingParticipant = { type: "", typeCanonical: "", typeReference: {}, role: {}, function: {} };
        if (!startingValue) {
            startingParticipant = {};
        } else {
            if (startingValue.extension) { startingParticipant.extension = startingValue.extension; }
            if (startingValue.type) { startingParticipant.type = startingValue.type; }
            if (startingValue.typeCanonical) { startingParticipant.typeCanonical = startingValue.typeCanonical; }
            if (startingValue.typeReference) { startingParticipant.typeReference = startingValue.typeReference; }
            if (startingValue.role) { startingParticipant.role = startingValue.role; }
            if (startingValue.function) { startingParticipant.function = startingValue.function; }
        }
        setParticipantState(JSON.parse(JSON.stringify(startingParticipant)));
    }, []);

    useEffect((() => {
        if (Object.keys(participantState).length) {
            let newParticipant = {};
            if (participantState.extension) { newParticipant.extension = participantState.extension; }
            if (participantState.type) { newParticipant.type = participantState.type; }
            if (participantState.typeCanonical) { newParticipant.typeCanonical = participantState.typeCanonical; }
            if (participantState.typeReference && Object.keys(participantState.typeReference).length) {
                newParticipant.typeReference = participantState.typeReference;
            }
            if (participantState.role && Object.keys(participantState.role).length) {
                newParticipant.role = participantState.role;
            }
            if (participantState.function && Object.keys(participantState.function).length) {
                newParticipant.function = participantState.function;
            }
            if (Object.keys(newParticipant).length === 0) {
                newParticipant = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newParticipant }; });
        }
    }), [participantState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.type && <p><b>Participant type: </b>{startingValue.type}</p>}
                {startingValue.typeCanonical && <p><b>Participant type canonical: </b><DisplayFromFHIRUri uri={startingValue.typeCanonical} /></p>}
                {startingValue.typeReference && <p><b>Participant type reference: </b><DisplayFromFHIRReference reference={startingValue.typeReference}
                    containedResourceJson={startingValue.typeReference.reference && containedResourceDictionary[startingValue.typeReference.reference]} /></p>}
                {startingValue.role && <span><b>Participant role: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.role} /></span>}
                {startingValue.function && <span><b>Participant function: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.function} /></span>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(participantState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeEntry elementName='type' fieldLabel='Participant type'
                    allowedValues={activityDefinitionDotParticipantDotTypeValues}
                    startingValue={participantState.type} setResourceState={setParticipantState} />
                <UriEntry elementName='typeCanonical' fieldLabel='Participant type canonical'
                    startingValue={participantState.typeCanonical} setResourceState={setParticipantState} />
                <ReferenceEntry elementName='typeReference' fieldLabel='Participant type reference'
                    referencedResourceTypes={activityDefinitionDotParticipantDotTypeReferenceResourceTypes}
                    startCollapsed
                    containedResourceDictionary={containedResourceDictionary}
                    startingValue={participantState.typeReference} setResourceState={setParticipantState} />
                <CodeableConceptEntry elementName='role' fieldLabel='Participant role'
                    systemChoices={activityDefinitionDotParticipantDotRoleSystemChoices} systemChoicesOpen
                    startCollapsed
                    startingValue={participantState.role} setResourceState={setParticipantState} />
                <CodeableConceptEntry elementName='function' fieldLabel='Participant function'
                    valueSet={activityDefinitionDotParticipantDotFunctionValueSet} startCollapsed
                    startingValue={participantState.function} setResourceState={setParticipantState} />
            </div>
        </>
    }
}

const ActivityDefinitionDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    const [activityDefinitionResourceState, setActivityDefinitionResourceState] = useState({});
    const [subjectDatatypeState, setSubjectDatatypeState] = useState("none");
    const [timingDatatypeState, setTimingDatatypeState] = useState("none");
    const [asNeededDatatypeState, setAsNeededDatatypeState] = useState("none");
    const [productDatatypeState, setProductDatatypeState] = useState("none");
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR ActivityDefinition - " + displayTitle));
        dispatch(changeTopNavigationElements(["Summary", "Title", "Activity Definition"]));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setActivityDefinitionResourceState(olderVersionJson);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            setActivityDefinitionResourceState(resourceJson);
            if (resourceJson.subjectReference) { setSubjectDatatypeState("Reference"); }
            else if (resourceJson.subjectCodeableConcept) { setSubjectDatatypeState("CodeableConcept"); }
            else if (resourceJson.subjectCanonical) { setSubjectDatatypeState("canonical"); }
            if (resourceJson.timingTiming) { setTimingDatatypeState("Timing"); }
            else if (resourceJson.timingAge) { setTimingDatatypeState("Age"); }
            else if (resourceJson.timingRange) { setTimingDatatypeState("Range"); }
            else if (resourceJson.timingDuration) { setTimingDatatypeState("Duration"); }
            if (resourceJson.asNeededCodeableConcept) { setAsNeededDatatypeState("CodeableConcept"); }
            else if (resourceJson.asNeededBoolean !== undefined) { setAsNeededDatatypeState("boolean"); }
            if (resourceJson.productReference) { setProductDatatypeState("Reference"); }
            else if (resourceJson.productCodeableConcept) { setProductDatatypeState("CodeableConcept"); }
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: ActivityDefinition " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect((() => {
        if (subjectDatatypeState === 'CodeableConcept') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'subjectReference': null, 'subjectCanonical': null } });
        }
        if (subjectDatatypeState === 'Reference') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'subjectCodeableConcept': null, 'subjectCanonical': null } });
        }
        if (subjectDatatypeState === 'canonical') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'subjectReference': null, 'subjectCodeableConcept': null } });
        }
    }), [subjectDatatypeState]);

    useEffect((() => {
        if (timingDatatypeState === 'Timing') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'timingAge': null, 'timingRange': null, 'timingDuration': null } });
        }
        if (timingDatatypeState === 'Age') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'timingTiming': null, 'timingRange': null, 'timingDuration': null } });
        }
        if (timingDatatypeState === 'Range') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'timingAge': null, 'timingTiming': null, 'timingDuration': null } });
        }
        if (timingDatatypeState === 'Duration') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'timingAge': null, 'timingRange': null, 'timingTiming': null } });
        }
    }), [timingDatatypeState]);

    useEffect((() => {
        if (asNeededDatatypeState === 'boolean') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'asNeededCodeableConcept': null } });
        }
        if (asNeededDatatypeState === 'CodeableConcept') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'asNeededBoolean': null } });
        }
    }), [asNeededDatatypeState]);

    useEffect((() => {
        if (productDatatypeState === 'Reference') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'productCodeableConcept': null } });
        }
        if (productDatatypeState === 'CodeableConcept') {
            setActivityDefinitionResourceState(prevState => { return { ...prevState, 'productReference': null } });
        }
    }), [productDatatypeState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(activityDefinitionResourceState, "text", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = null;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = activityDefinitionResourceState.title;
            newResourceDetail.displayTitle = activityDefinitionResourceState.title ||
                activityDefinitionResourceState.name || newResourceDetail.displayTitle;
            if (activityDefinitionResourceState.title !== undefined &&
                previousFinalValue !== activityDefinitionResourceState.title) {
                console.log("resourceUpdate for ActivityDefinition.title");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [activityDefinitionResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = null;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = activityDefinitionResourceState.name;
            newResourceDetail.displayTitle = activityDefinitionResourceState.title ||
                activityDefinitionResourceState.name || newResourceDetail.displayTitle;
            if (activityDefinitionResourceState.name !== undefined &&
                previousFinalValue !== activityDefinitionResourceState.name) {
                console.log("resourceUpdate for ActivityDefinition.name");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [activityDefinitionResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "subtitle", "string", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.subtitle]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "subjectCodeableConcept", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.subjectCodeableConcept]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "subjectReference", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.subjectReference]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "subjectCanonical", "canonical", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.subjectCanonical]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "library", "ArrayOfUri", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.library]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "kind", "code", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.kind]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "profile", "uri", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.profile]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "code", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.code]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "intent", "code", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.intent]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "priority", "code", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.priority]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "doNotPerform", "boolean", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.doNotPerform]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "timingTiming", "Timing", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.timingTiming]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "timingAge", "Quantity", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.timingAge]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "timingRange", "Range", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.timingRange]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "timingDuration", "Quantity", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.timingDuration]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "asNeededCodeableConcept", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.asNeededCodeableConcept]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "asNeededBoolean", "boolean", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.asNeededBoolean]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "location", "CodeableReference", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.location]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "participant", "ArrayOfActivityDefinitionParticipant", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.participant]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "productCodeableConcept", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.productCodeableConcept]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "productReference", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.productReference]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "quantity", "Quantity", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.quantity]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "dosage", "ArrayOfDosage", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.dosage]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "dosageInstruction", "DosageInstruction", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.dosageInstruction]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "bodySite", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.bodySite]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "specimenRequirement", "ArrayOfUri", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.specimenRequirement]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "observationRequirement", "ArrayOfUri", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.observationRequirement]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "observationResultRequirement", "ArrayOfUri", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.observationResultRequirement]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "transform", "uri", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.transform]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(activityDefinitionResourceState, "dynamicValue", "ArrayOfActivityDefinitionDynamicValue", parentResourceDetail,
                dispatch);
        }
    }, [activityDefinitionResourceState.dynamicValue]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {activityDefinitionResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="summary">Summary</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <NarrativeEntry elementName="text"
                            startingValue={activityDefinitionResourceState.text}
                            generatedNarrative={generateNarrativeForActivityDefinitionResource(activityDefinitionResourceState)}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>{(activityDefinitionResourceState.text?.div &&
                            activityDefinitionResourceState.text.status &&
                            activityDefinitionResourceState.text.status !== "empty") ?
                            <DisplayFromFHIRXhtml xhtml={activityDefinitionResourceState.text.div} />
                            : <p>[Narrative summary not generated.]</p>}</>
                    }
                </div>
                <h3 id="title">Title</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <StringEntry elementName="title" fieldLabel="Title"
                            startingValue={activityDefinitionResourceState.title}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>{activityDefinitionResourceState.title &&
                            <p><b>Title: </b>{activityDefinitionResourceState.title}</p>}</>
                    }
                    {editMode ?
                        <StringEntry elementName="subtitle" fieldLabel="Subtitle"
                            startingValue={activityDefinitionResourceState.subtitle}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>{activityDefinitionResourceState.subtitle &&
                            <p><b>Subtitle: </b>{activityDefinitionResourceState.subtitle}</p>}</>
                    }
                    {editMode ?
                        <StringEntry elementName="name" fieldLabel="Name"
                            startingValue={activityDefinitionResourceState.name}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>{activityDefinitionResourceState.name &&
                            <p><b>Name: </b>{activityDefinitionResourceState.name}</p>}</>
                    }
                    {activityDefinitionResourceState.description && <div>
                        <b>Description: </b><DisplayFromFHIRMarkdown
                            markdown={activityDefinitionResourceState.description} />
                        {editMode ?
                            <p>(Description content can be edited in the Metadata.)</p> :
                            <><br /><br /></>}
                    </div>}
                </div>
                <h3 id="activity-definition">Activity Definition</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <p><b>Subject:</b></p>
                            <div style={{ marginLeft: "24px" }}>
                                <DatatypeSelector elementXName='subject[x]'
                                    allowedDatatypes={activityDefinitionDotSubjectAllowedDatatypes}
                                    datatypeState={subjectDatatypeState}
                                    setDatatypeState={setSubjectDatatypeState} />
                                {(subjectDatatypeState === 'CodeableConcept') &&
                                    <CodeableConceptEntry elementName='subjectCodeableConcept'
                                        fieldLabel='Subject (as CodeableConcept)'
                                        startingValue={activityDefinitionResourceState.subjectCodeableConcept || null}
                                        valueSet={activityDefinitionDotSubjectCodeableConceptValueSet}
                                        setResourceState={setActivityDefinitionResourceState} />}
                                {(subjectDatatypeState === 'Reference') &&
                                    <ReferenceEntry elementName='subjectReference'
                                        containedResourceDictionary={containedResourceDictionary}
                                        fieldLabel='Subject (as Reference)'
                                        referencedResourceTypes={activityDefinitionDotSubjectReferenceResourceTypes}
                                        startingValue={activityDefinitionResourceState.subjectReference || null}
                                        setResourceState={setActivityDefinitionResourceState} />}
                                {(subjectDatatypeState === 'canonical') &&
                                    <UriEntry elementName='subjectCanonical' fieldLabel='Subject (as Canonical URI)'
                                        startingValue={activityDefinitionResourceState.subjectCanonical || null}
                                        setResourceState={setActivityDefinitionResourceState} />}
                            </div>
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.subjectCodeableConcept &&
                                <span><b>Subject: </b><DisplayFromFHIRCodeableConcept
                                    codeableConcept={activityDefinitionResourceState.subjectCodeableConcept} /></span>}
                            {activityDefinitionResourceState.subjectReference &&
                                <div><b>Subject: </b><DisplayFromFHIRReference
                                    reference={activityDefinitionResourceState.subjectReference} /><br /></div>}
                            {activityDefinitionResourceState.subjectCanonical &&
                                <p><b>Subject: </b><DisplayFromFHIRUri uri={activityDefinitionResourceState.subjectCanonical} /></p>}
                        </>
                    }
                    {editMode ?
                        <ArrayEntry datatype='uri' elementName='library' fieldLabel='Library'
                            startingValue={activityDefinitionResourceState.library}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>
                            {Array.isArray(activityDefinitionResourceState.library) &&
                                <>
                                    {activityDefinitionResourceState.library.map((library, libraryIndex) => {
                                        return <div key={libraryIndex}>
                                            <b>Library: </b><DisplayFromFHIRUri uri={library} />
                                        </div>
                                    })}
                                    <br />
                                </>}
                        </>
                    }
                    {editMode ?
                        <CodeEntry elementName='kind' fieldLabel='Kind of Resource'
                            startingValue={activityDefinitionResourceState.kind}
                            setResourceState={setActivityDefinitionResourceState}
                            allowedValues={activityDefinitionDotKindValues}
                            dataEntryStyle='dropdown' />
                        :
                        <>{activityDefinitionResourceState.kind &&
                            <p><b>Kind of Resource: </b>{activityDefinitionResourceState.kind}</p>}</>
                    }
                    {editMode ?
                        <UriEntry elementName='profile' fieldLabel='Profile'
                            startingValue={activityDefinitionResourceState.profile}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>
                            {activityDefinitionResourceState.profile &&
                                <p><b>Profile: </b><DisplayFromFHIRUri uri={activityDefinitionResourceState.profile} /></p>}</>
                    }
                    {editMode ?
                        <CodeableConceptEntry elementName='code' fieldLabel='Code'
                            startCollapsed systemChoices={activityDefinitionDotCodeSystemChoices}
                            systemChoicesOpen={true}
                            startingValue={activityDefinitionResourceState.code}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>
                            {activityDefinitionResourceState.code &&
                                <p><b>Code: </b><DisplayFromFHIRCodeableConcept
                                    codeableConcept={activityDefinitionResourceState.code} /></p>}
                        </>
                    }
                    {editMode ?
                        <CodeEntry elementName='intent' fieldLabel='Intent'
                            startingValue={activityDefinitionResourceState.intent}
                            setResourceState={setActivityDefinitionResourceState}
                            allowedValues={activityDefinitionDotIntentValues} />
                        :
                        <>{activityDefinitionResourceState.intent && <p><b>Intent: </b>{activityDefinitionResourceState.intent}</p>}</>
                    }
                    {editMode ?
                        <CodeEntry elementName='priority' fieldLabel='Priority'
                            startingValue={activityDefinitionResourceState.priority}
                            setResourceState={setActivityDefinitionResourceState}
                            allowedValues={activityDefinitionDotPriorityValues} />
                        :
                        <>{activityDefinitionResourceState.priority &&
                            <p><b>Priority: </b>{activityDefinitionResourceState.priority}</p>}</>
                    }
                    {editMode ?
                        <BooleanEntry elementName='doNotPerform' fieldLabel='DO NOT PEFORM'
                            startingValue={activityDefinitionResourceState.doNotPerform}
                            setResourceState={setActivityDefinitionResourceState} />
                        :
                        <>{activityDefinitionResourceState.doNotPerform && <p><b>Negation: DO NOT PEFORM</b></p>}</>
                    }

                    {editMode ?
                        <>
                            <p><b>Timing:</b></p>
                            <div style={{ marginLeft: "24px" }}>
                                <DatatypeSelector elementXName='timing[x]' allowedDatatypes={activityDefinitionDotTimingAllowedDatatypes}
                                    datatypeState={timingDatatypeState} setDatatypeState={setTimingDatatypeState} />
                                {(timingDatatypeState === 'Timing') &&
                                    <TimingEntry elementName='timingTiming' startCollapsed
                                        fieldLabel='Timing' startingValue={activityDefinitionResourceState.timingTiming || null}
                                        setResourceState={setActivityDefinitionResourceState} />}
                                {(timingDatatypeState === 'Age') &&
                                    <QuantityEntry dataEntryStyle='Age' elementName='timingAge' fieldLabel='Timing (Age)'
                                        startingValue={activityDefinitionResourceState.timingAge || null}
                                        setResourceState={setActivityDefinitionResourceState}
                                        startCollapsed />}
                                {(timingDatatypeState === 'Range') &&
                                    <RangeEntry elementName='timingRange' fieldLabel='Timing (Range)'
                                        startingValue={activityDefinitionResourceState.timingRange || null}
                                        setResourceState={setActivityDefinitionResourceState}
                                        dataEntryStyle='Duration' startCollapsed />}
                                {(timingDatatypeState === 'Duration') &&
                                    <QuantityEntry dataEntryStyle='Duration' elementName='timingDuration'
                                        fieldLabel='Timing (Duration)'
                                        startingValue={activityDefinitionResourceState.timingDuration || null}
                                        setResourceState={setActivityDefinitionResourceState}
                                        startCollapsed />}
                            </div>
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.timingTiming &&
                                <p><b>Timing: </b>{displayStringFromFHIRTiming(activityDefinitionResourceState.timingTiming)}</p>}
                            {activityDefinitionResourceState.timingAge &&
                                <p><b>Timing: </b>{displayStringFromFHIRQuantity(activityDefinitionResourceState.timingAge)}</p>}
                            {activityDefinitionResourceState.timingRange &&
                                <p><b>Timing: </b>{displayStringFromFHIRRange(activityDefinitionResourceState.timingRange)}</p>}
                            {activityDefinitionResourceState.timingDuration &&
                                <p><b>Timing: </b>{displayStringFromFHIRQuantity(activityDefinitionResourceState.timingDuration)}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <p><b>As Needed:</b></p>
                            <div style={{ marginLeft: "24px" }}>
                                <DatatypeSelector elementXName='asNeeded[x]' allowedDatatypes={activityDefinitionDotAsNeededAllowedDatatypes}
                                    datatypeState={asNeededDatatypeState} setDatatypeState={setAsNeededDatatypeState} />
                                {(asNeededDatatypeState === 'boolean') &&
                                    <BooleanEntry elementName='asNeededBoolean' fieldLabel='As needed'
                                        startingValue={activityDefinitionResourceState.asNeededBoolean ?? null}
                                        setResourceState={setActivityDefinitionResourceState}
                                        storeFalse={true} />}
                                {(asNeededDatatypeState === 'CodeableConcept') &&
                                    <CodeableConceptEntry elementName='asNeededCodeableConcept'
                                        startCollapsed
                                        fieldLabel='As needed (reason)'
                                        startingValue={activityDefinitionResourceState.asNeededCodeableConcept || null}
                                        systemChoices={activityDefinitionDotAsNeededCodeableConceptSystemChoices} systemChoicesOpen
                                        setResourceState={setActivityDefinitionResourceState} />}
                            </div>
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.asNeededBoolean !== undefined &&
                                <p><b>As needed: </b>{displayStringFromFHIRBoolean(activityDefinitionResourceState.asNeededBoolean)}</p>}
                            {activityDefinitionResourceState.asNeededCodeableConcept &&
                                <span><b>As needed for: </b><DisplayFromFHIRCodeableConcept codeableConcept={activityDefinitionResourceState.asNeededCodeableConcept} /></span>}
                        </>
                    }
                    {editMode ?
                        <>
                            <CodeableReferenceEntry elementName='location' fieldLabel='Location'
                                containedResourceDictionary={containedResourceDictionary}
                                referencedResourceTypes={activityDefinitionDotLocationResourceTypes}
                                enableCreation={true} startCollapsed
                                startingValue={activityDefinitionResourceState.location}
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.location &&
                                <p><b>Location: </b><DisplayFromFHIRCodeableReference codeableReference={activityDefinitionResourceState.location}
                                    containedResourceDictionary={containedResourceDictionary} /></p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype='ActivityDefinitionParticipant' elementName='participant'
                                startCollapsed containedResourceDictionary={containedResourceDictionary}
                                fieldLabel='Participant(s)' startingValue={activityDefinitionResourceState.participant}
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.participant?.length > 0 && <div>
                                <p>{activityDefinitionResourceState.participant.length === 1 ? <b>Participant: </b> : <b>Participants: </b>}</p>
                                <div style={{ marginLeft: "24px" }}>
                                    {activityDefinitionResourceState.participant.map((participant, participantIndex) => {
                                        return <div key={participantIndex}>
                                            <p><b>Participant entry {participantIndex + 1}</b></p>
                                            <div style={{ marginLeft: "24px" }}>
                                                {participant.type && <p><b>Participant type: </b>{participant.type}</p>}
                                                {participant.typeCanonical && <p><b>Participant type canonical: </b><DisplayFromFHIRUri uri={participant.typeCanonical} /></p>}
                                                {participant.typeReference && <p><b>Participant type reference: </b><DisplayFromFHIRReference reference={participant.typeReference}
                                                    containedResourceJson={participant.typeReference.reference && containedResourceDictionary[participant.typeReference.reference]} /></p>}
                                                {participant.role && <span><b>Participant role: </b><DisplayFromFHIRCodeableConcept codeableConcept={participant.role} /></span>}
                                                {participant.function && <span><b>Participant function: </b><DisplayFromFHIRCodeableConcept codeableConcept={participant.function} /></span>}
                                            </div>
                                        </div>
                                    })}
                                </div>
                            </div>
                            }
                        </>
                    }
                    {editMode ?
                        <>
                            <p><b>Product:</b></p>
                            <div style={{ marginLeft: "24px" }}>
                                <DatatypeSelector elementXName='product[x]'
                                    allowedDatatypes={activityDefinitionDotProductAllowedDatatypes}
                                    datatypeState={productDatatypeState} setDatatypeState={setProductDatatypeState} />
                                {(productDatatypeState === 'Reference') &&
                                    <ReferenceEntry elementName='productReference'
                                        containedResourceDictionary={containedResourceDictionary}
                                        fieldLabel='Product (as Reference)' startCollapsed
                                        referencedResourceTypes={activityDefinitionDotProductReferenceResourceTypes}
                                        startingValue={activityDefinitionResourceState.productReference || null}
                                        setResourceState={setActivityDefinitionResourceState} />}
                                {(productDatatypeState === 'CodeableConcept') &&
                                    <CodeableConceptEntry elementName='productCodeableConcept'
                                        fieldLabel='Product (as CodeableConcept)' startCollapsed
                                        startingValue={activityDefinitionResourceState.productCodeableConcept || null}
                                        systemChoices={activityDefinitionDotProductCodeableConceptSystemChoices} systemChoicesOpen={true}
                                        setResourceState={setActivityDefinitionResourceState} />}
                            </div>
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.productReference &&
                                <p><b>Product: </b><DisplayFromFHIRReference reference={activityDefinitionResourceState.productReference}
                                    containedResourceJson={activityDefinitionResourceState.productReference.reference && containedResourceDictionary[activityDefinitionResourceState.productReference.reference]} /></p>}
                            {activityDefinitionResourceState.productCodeableConcept &&
                                <span><b>Product: </b><DisplayFromFHIRCodeableConcept codeableConcept={activityDefinitionResourceState.productCodeableConcept} /></span>}
                        </>
                    }
                    {editMode ?
                        <>
                            <QuantityEntry elementName='quantity' fieldLabel='Quantity'
                                startingValue={activityDefinitionResourceState.quantity}
                                setResourceState={setActivityDefinitionResourceState}
                                startCollapsed dataEntryStyle='SimpleQuantity' valueSet='UCUM' />
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.quantity &&
                                <p><b>Quantity: </b>{displayStringFromFHIRQuantity(activityDefinitionResourceState.quantity)}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <DosageInstructionEntry elementName='dosageInstruction' fieldLabel='Dosage'
                                startCollapsed containedResourceDictionary={containedResourceDictionary}
                                startingValue={activityDefinitionResourceState.dosageInstruction}
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {(activityDefinitionResourceState.dosageInstruction) &&
                                <p><b>Dosage: </b>{displayStringFromFHIRDosageInstruction(activityDefinitionResourceState.dosageInstruction)}</p>}
                        </>
                    }
                    <>
                        {(activityDefinitionResourceState.dosage?.length === 1) &&
                            <p><b>Dosage: </b>{displayStringFromFHIRDosage(activityDefinitionResourceState.dosage[0])}</p>}
                        {(activityDefinitionResourceState.dosage?.length > 1) && activityDefinitionResourceState.dosage.map((dosage, dosageIndex) => {
                            return <div key={dosageIndex}><div><b>Dosage: </b>{displayStringFromFHIRDosage(dosage)}</div></div>
                        })}
                    </>
                    {editMode ?
                        <>
                            <ArrayEntry datatype='CodeableConcept'
                                elementName='bodySite' fieldLabel='Body Site'
                                startingValue={activityDefinitionResourceState.bodySite}
                                systemChoices={activityDefinitionDotBodySiteSystemChoices}
                                systemChoicesOpen startCollapsed
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {(activityDefinitionResourceState.bodySite?.length === 1) &&
                                <span><b>Body Site: </b><DisplayFromFHIRCodeableConcept codeableConcept={activityDefinitionResourceState.bodySite[0]} /></span>}
                            {(activityDefinitionResourceState.bodySite?.length > 1) && activityDefinitionResourceState.bodySite.map((bodySite, bodySiteIndex) => {
                                return <div key={bodySiteIndex}>
                                    <div><b>Body Site: </b><DisplayFromFHIRCodeableConcept codeableConcept={bodySite} /></div>
                                </div>
                            })}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype='uri' elementName='specimenRequirement'
                                fieldLabel='Specimen Requirement'
                                startingValue={activityDefinitionResourceState.specimenRequirement}
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {Array.isArray(activityDefinitionResourceState.specimenRequirement) &&
                                activityDefinitionResourceState.specimenRequirement.map((specimenRequirement, index) => {
                                    return <div key={index}><b>Specimen Requirement: </b><DisplayFromFHIRUri uri={specimenRequirement} /></div>
                                })}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype='uri' elementName='observationRequirement'
                                fieldLabel='Observation Requirement'
                                startingValue={activityDefinitionResourceState.observationRequirement}
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {Array.isArray(activityDefinitionResourceState.observationRequirement) &&
                                activityDefinitionResourceState.observationRequirement.map((observationRequirement, index) => {
                                    return <div key={index}><b>Observation Requirement: </b><DisplayFromFHIRUri uri={observationRequirement} /></div>
                                })}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype='uri' elementName='observationResultRequirement'
                                fieldLabel='Observation Result Requirement'
                                startingValue={activityDefinitionResourceState.observationResultRequirement}
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {Array.isArray(activityDefinitionResourceState.observationResultRequirement) &&
                                activityDefinitionResourceState.observationResultRequirement.map((observationResultRequirement, index) => {
                                    return <div key={index}><b>Observation Result Requirement: </b><DisplayFromFHIRUri uri={observationResultRequirement} /></div>
                                })}
                        </>
                    }
                    {editMode ?
                        <>
                            <UriEntry elementName='transform' fieldLabel='Transform'
                                startingValue={activityDefinitionResourceState.transform}
                                setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.transform &&
                                <p><b>Transform: </b><DisplayFromFHIRUri uri={activityDefinitionResourceState.transform} /></p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype='ActivityDefinitionDynamicValue' elementName='dynamicValue'
                                fieldLabel='Dynamic Value'
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={activityDefinitionResourceState.dynamicValue}
                                startCollapsed setResourceState={setActivityDefinitionResourceState} />
                        </>
                        :
                        <>
                            {activityDefinitionResourceState.dynamicValue?.length > 0 && <div>
                                <p>{activityDefinitionResourceState.dynamicValue.length === 1 ? <b>Dynamic Value: </b> : <b>Dynamic Values: </b>}</p>
                                <div style={{ marginLeft: "24px" }}>
                                    {activityDefinitionResourceState.dynamicValue.map((dynamicValue, dynamicValueIndex) => {
                                        return <div key={dynamicValueIndex}>
                                            {dynamicValue.path && <p><b>Path: </b>{dynamicValue.path}</p>}
                                            {dynamicValue.expression &&
                                                <div><b>Expression: </b><DisplayFromFHIRExpression expression={dynamicValue.expression} /></div>}
                                            <br />
                                        </div>
                                    })}
                                </div>
                            </div>
                            }
                        </>
                    }
                </div>
            </div>
        </>}
    </div>
}

export default ActivityDefinitionDisplay;
export { ActivityDefinitionDynamicValueEntry, ActivityDefinitionParticipantEntry, activityDefinitionDotSubjectCodeableConceptValueSet };